import React, {ReactNode, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {CircularProgress} from '@mui/material';
import {useSearchParams} from 'react-router-dom';
import './ListPageKit.css';

/*
 * The list pages (Clients, Appointments, Estimates, Jobs, Invoices, Equipment,
 * Tools & Gear, Team Members), built to match Android's ListPageKit.kt: title
 * row, labeled filter/search box, record cards, status pill, notes, and the
 * list/map tab.
 *
 * Every layout decision is made from the width of the element itself (the
 * page area, the filter box, the list), measured with a ResizeObserver, never
 * from the window: collapsing the side menu or opening the map beside a list
 * changes these widths without the window changing at all.
 */

// ---- Measuring ------------------------------------------------------------

/** A callback ref plus the element's current content-box width (0 until measured). */
export function useContentWidth<T extends HTMLElement>(): [(element: T | null) => void, number] {
  const [element, setElement] = useState<T | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!element) return;
    const read = () => setWidth(element.clientWidth
      - parseFloat(getComputedStyle(element).paddingLeft || '0')
      - parseFloat(getComputedStyle(element).paddingRight || '0'));
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return [setElement, width];
}

// ---- Title row --------------------------------------------------------------

/**
 * The page title with its button right beside it (16px apart, never pushed to
 * the far side). A wrapping row, like Android's ListPageTitleRow: when the two
 * don't fit on one line, the button drops under the title.
 */
export const ListPageTitleRow = ({title, button, style}: {title: string; button?: ReactNode; style?: React.CSSProperties}) => (
  <div className="lp-title-row" style={style}>
    <h1 className="lp-title">{title}</h1>
    {button}
  </div>
);

/** The green title-row pill ("Add New Client", …). */
export const AddButton = ({label, onClick}: {label: string; onClick: () => void}) => (
  <button type="button" className="lp-button lp-button--add" onClick={onClick}>{label}</button>
);

/** The white, green-outlined title-row button ("Set automatic follow-ups"). */
export const SecondaryActionButton = ({label, onClick}: {label: string; onClick: () => void}) => (
  <button type="button" className="lp-button lp-button--secondary" onClick={onClick}>{label}</button>
);

// ---- Outline box, chips, search -------------------------------------------------

/**
 * The outlined box with its label sitting on the top border (Android's
 * LabeledOutlineBox). Always the full width of the page area, so it sits over
 * the cards like a header even when its contents use only part of it.
 */
export const LabeledOutlineBox = ({label, children, style}: {label: string; children: ReactNode; style?: React.CSSProperties}) => (
  <div className="lp-outline" style={style}>
    <div className="lp-outline-box">{children}</div>
    <div className="lp-outline-label">{label}</div>
  </div>
);

/** One filter chip. While searching every chip dims and none is green. */
export const FilterChip = ({label, active, searching, onClick, activeBold}: {
  label: string; active: boolean; searching: boolean; onClick: () => void;
  /** The map's filter strip writes its active chip in bold, as on Android. */
  activeBold?: boolean;
}) => (
  <button
    type="button"
    aria-pressed={active}
    className={['lp-chip', active && 'lp-chip--active', active && activeBold && 'lp-chip--bold', searching && 'lp-chip--dimmed'].filter(Boolean).join(' ')}
    onClick={onClick}
  >
    {label}
  </button>
);

/** The search never gets narrower than this side by side, so the whole placeholder shows. */
const SIDE_BY_SIDE_MIN_SEARCH = 320;
/** The vertical divider: 1px with 12px either side. */
const SIDE_BY_SIDE_DIVIDER = 25;
const CHIP_GAP = 8;

export const SEARCH_PLACEHOLDER = 'Search by name, phone, or address...';

/**
 * The filter/search box, in one of two fixed layouts, never wrapping freely:
 *  - stacked (Android): chips, a thin horizontal line, the search under them;
 *  - side by side: chips, a thin vertical line, then the search right after
 *    them, the empty space at the right end. Only when every chip fits on one
 *    line and the search still gets SIDE_BY_SIDE_MIN_SEARCH.
 * A box without chips holds only the search, on the left, up to 500px.
 */
export const FilterSearchBox = ({label, chips, activeChip, onChipChange, query, onQueryChange, placeholder = SEARCH_PLACEHOLDER}: {
  label: string;
  chips: string[];
  activeChip?: string;
  onChipChange: (chip: string) => void;
  query: string;
  onQueryChange: (query: string) => void;
  placeholder?: string;
}) => {
  const [boxRef, innerWidth] = useContentWidth<HTMLDivElement>();
  const chipsRef = useRef<HTMLDivElement | null>(null);
  const [chipsWidth, setChipsWidth] = useState(0);
  const searching = query.trim().length > 0;

  // The chips' one-line width: each chip's own width (they never shrink) plus
  // the gaps. Measured, since it depends on the font.
  useLayoutEffect(() => {
    const element = chipsRef.current;
    if (!element) return;
    const children = Array.from(element.children) as HTMLElement[];
    const total = children.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0)
      + CHIP_GAP * Math.max(0, children.length - 1);
    setChipsWidth(total);
  }, [chips, innerWidth]);

  const sideBySide = chips.length > 0 && innerWidth > 0
    && chipsWidth + SIDE_BY_SIDE_DIVIDER + SIDE_BY_SIDE_MIN_SEARCH <= innerWidth;

  return (
    <LabeledOutlineBox label={label}>
      <div ref={boxRef} className={`lp-filter ${sideBySide ? 'lp-filter--side' : 'lp-filter--stacked'}`}>
        {chips.length > 0 && (
          <>
            <div ref={chipsRef} className="lp-chips" role="group" aria-label="Filters">
              {chips.map(chip => (
                <FilterChip
                  key={chip}
                  label={chip}
                  active={chip === activeChip}
                  searching={searching}
                  onClick={() => onChipChange(chip)}
                />
              ))}
            </div>
            <div className="lp-divider" role="presentation"/>
          </>
        )}
        <input
          className="lp-search"
          type="search"
          value={query}
          onChange={event => onQueryChange(event.target.value)}
          placeholder={placeholder}
          aria-label={label}
        />
      </div>
    </LabeledOutlineBox>
  );
};

// ---- Status list state ------------------------------------------------------

/** A filter chip, and the `?filters=` value the side menu links to it with. */
export interface ChipDef {
  label: string;
  urlParam: string;
}

/**
 * The chip/search state with Android StatusListPage's rules: one chip active
 * at a time, opening on the first (or on the menu's `?filters=` link);
 * typing searches every record whatever its chip; picking a chip clears the
 * search. Search matches any part of the card's text, not just the start of a
 * word.
 */
export function useStatusList(rows: ListRow[], chips: ChipDef[]) {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = chips.find(chip => (searchParams.get('filters') ?? '').split(',').includes(chip.urlParam));
  const activeChip = (fromUrl ?? chips[0])?.label;
  const [query, setQuery] = useState('');

  // keepQuery: the map's own filter strip (the full map under 900px) picks a
  // chip without touching what is typed in the list's search.
  const setActiveChip = useCallback((label: string, keepQuery = false) => {
    const chip = chips.find(c => c.label === label);
    if (!chip) return;
    if (!keepQuery) setQuery('');
    const next = new URLSearchParams(searchParams);
    next.set('filters', chip.urlParam);
    setSearchParams(next, {replace: true});
  }, [chips, searchParams, setSearchParams]);

  /** The active chip's rows whatever is typed: what the full map shows, as on Android. */
  const inChip = useMemo(
    () => chips.length === 0 ? rows : rows.filter(row => row.chip === activeChip),
    [rows, chips.length, activeChip],
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter(row => rowMatches(row, q)) : inChip;
  }, [rows, query, inChip]);

  return {
    chips: chips.map(chip => chip.label),
    activeChip,
    setActiveChip,
    query,
    setQuery,
    shown,
    inChip,
  };
}

export type StatusListState = ReturnType<typeof useStatusList>;

export const valueText = (value: CardValue | undefined): string =>
  value == null ? '' : typeof value === 'string' ? value : value.value;

const rowMatches = (row: ListRow, q: string): boolean => {
  const texts = [row.title, ...Object.values(row.values).map(valueText), row.searchText ?? ''];
  if (texts.join(' ').toLowerCase().includes(q)) return true;
  // A phone number typed as bare digits ("2085550142") still finds "(208) 555-0142".
  if (/^[\d\s()+.-]+$/.test(q)) {
    const digits = q.replace(/\D/g, '');
    return digits.length >= 3 && texts.some(text => text.replace(/\D/g, '').includes(digits));
  }
  return false;
};

// ---- The link between a map and the list beside it -------------------------------

export type RowKey = string | number;

/**
 * What a list and the map beside it tell each other (MapListPage provides it):
 * the pin whose popup is open lights its card and scrolls the list to it, and
 * the card under the mouse makes its pin stand out. Only in the split; under
 * 900px the map and the list are never on screen together.
 */
export interface MapListLink {
  /** The map is open beside the list (false: the map alone, or no map). */
  split: boolean;
  activeKey: RowKey | null;
  setActiveKey: (key: RowKey | null) => void;
  hoveredKey: RowKey | null;
  setHoveredKey: (key: RowKey | null) => void;
}

const MapListContext = createContext<MapListLink | null>(null);

/** Null outside a MapListPage. */
export const useMapListLink = () => useContext(MapListContext);

// ---- Cards --------------------------------------------------------------------

/** Who (name, then how to reach them), Details (where, when, what), Money (under the status). */
export type CardGroup = 'who' | 'details' | 'money';

/** A plain line, or a labeled one ("Job total" + "$3,400") like Android's FootLine. */
export type CardValue = string | {label: string; value: string; muted?: boolean};

export interface CardSlot {
  key: string;
  group: CardGroup;
  /** The slot's longest realistic value in px, at the card's font. */
  width: number;
}

/**
 * One page's card: its slots in Android's stacked order (the title and the
 * status are not slots), and the realistic widths the wider layouts are
 * judged by. Grouped and one-row order the slots by group.
 */
export interface CardSpec {
  /** The longest realistic title in px (18px Roboto). */
  title: number;
  /** The widest status pill in px; 0 for a card that never has one. */
  status: number;
  slots: CardSlot[];
}

export interface ListRow {
  key: string | number;
  title: string;
  values: Partial<Record<string, CardValue>>;
  status?: string;
  /** The chip this row files under; a row under no chip only shows in a search. */
  chip?: string | null;
  /** Text the search matches that the card does not show. */
  searchText?: string;
  onClick?: () => void;
  /** On a page with a map: the job site. A record without one has no pin and is listed as not on the map. */
  location?: {lat: number; lng: number} | null;
  /** What a pin's popup says under the name (the appointment's time, the job's total). */
  mapLine?: string;
}

export type CardLayout = 'stacked' | 'grouped' | 'row';

// The card's chrome around the columns (see .lp-card in ListPageKit.css): the
// 5px accent and 10.5px padding on the left; the padding, the chevron's 24px
// and its 6px gap, and the 1.5px border on the right.
const CARD_CHROME_LEFT = 15.5;
const CARD_CHROME_RIGHT = 42;
/** The least space between two columns; extra width is split into these gaps. */
const COLUMN_GAP = 24;

interface Column {
  kind: 'title' | 'slot' | 'status' | 'group';
  group?: CardGroup;
  slot?: CardSlot;
  width: number;
}

const GROUP_ORDER: CardGroup[] = ['who', 'details', 'money'];

const slotsInGroupOrder = (spec: CardSpec) =>
  GROUP_ORDER.flatMap(group => spec.slots.filter(slot => slot.group === group));

/** One row: title, every slot in group order, then the status at the far right. */
const rowColumns = (spec: CardSpec): Column[] => [
  {kind: 'title', width: spec.title},
  ...slotsInGroupOrder(spec).map(slot => ({kind: 'slot' as const, slot, width: slot.width})),
  ...(spec.status > 0 ? [{kind: 'status' as const, width: spec.status}] : []),
];

/** Grouped: one column per group that has anything in it, each as wide as its widest field. */
const groupedColumns = (spec: CardSpec): Column[] => GROUP_ORDER
  .map(group => {
    const widths = spec.slots.filter(slot => slot.group === group).map(slot => slot.width);
    if (group === 'who') widths.push(spec.title);
    if (group === 'money' && spec.status > 0) widths.push(spec.status);
    return {kind: 'group' as const, group, width: widths.length ? Math.max(...widths) : 0};
  })
  .filter(column => column.width > 0);

/** The narrowest list a set of columns fits: every column at its realistic width, the least gaps, the card's chrome. */
const columnsMinWidth = (columns: Column[]) =>
  columns.reduce((sum, column) => sum + column.width, 0)
  + COLUMN_GAP * Math.max(0, columns.length - 1)
  + CARD_CHROME_LEFT + CARD_CHROME_RIGHT;

/**
 * The switch points for a card: one row from this width, grouped from that,
 * stacked below. Derived from the spec's realistic widths, so a page's switch
 * points are written down where its widths are (each page's CARD_SPEC).
 */
export const cardSwitchPoints = (spec: CardSpec) => ({
  row: Math.ceil(columnsMinWidth(rowColumns(spec))),
  grouped: Math.ceil(columnsMinWidth(groupedColumns(spec))),
});

/**
 * The shared column template: each column at least its realistic width (plus
 * the card's chrome on the outer two, which a subgrid hands to its edge
 * tracks) and at most its widest actual value.
 */
const gridTemplate = (columns: Column[]) => columns
  .map((column, index) => {
    const chrome = (index === 0 ? CARD_CHROME_LEFT : 0) + (index === columns.length - 1 ? CARD_CHROME_RIGHT : 0);
    return `minmax(${Math.ceil(column.width + chrome)}px, max-content)`;
  })
  .join(' ');

const Chevron = () => (
  <svg className="lp-chevron" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 3L17 12L8 21" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const StatusPill = ({text, className}: {text: string; className?: string}) => (
  <span className={['lp-pill', className].filter(Boolean).join(' ')}>{text}</span>
);

const Value = ({value}: {value: CardValue | undefined}) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return <div className="lp-line">{value}</div>;
  return (
    <div className="lp-line">
      {value.label}{' '}
      <span className={value.muted ? 'lp-line-value lp-line-value--muted' : 'lp-line-value'}>{value.value}</span>
    </div>
  );
};

const Card = ({row, spec, layout, active, onHover}: {
  row: ListRow;
  spec: CardSpec;
  layout: CardLayout;
  /** Beside a map: this card's pin has its popup open. */
  active?: boolean;
  /** Beside a map: the mouse came onto (true) or left (false) this card. */
  onHover?: (over: boolean) => void;
}) => {
  const clickable = row.onClick != null;
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (active) cardRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
  }, [active]);
  const cardProps = {
    ref: cardRef,
    className: ['lp-card', clickable && 'lp-card--clickable', active && 'lp-card--active'].filter(Boolean).join(' '),
    ...(onHover ? {
      onMouseEnter: () => onHover(true),
      onMouseLeave: () => onHover(false),
    } : {}),
    ...(clickable ? {
      role: 'button',
      tabIndex: 0,
      onClick: row.onClick,
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          row.onClick?.();
        }
      },
    } : {}),
  };
  const title = <div className="lp-card-title">{row.title}</div>;

  if (layout === 'stacked') {
    return (
      <div {...cardProps}>
        <div className="lp-card-title">
          {row.status && <StatusPill text={row.status} className="lp-card-status"/>}
          {row.title}
        </div>
        {spec.slots.map(slot => <Value key={slot.key} value={row.values[slot.key]}/>)}
        {clickable && <Chevron/>}
      </div>
    );
  }

  if (layout === 'grouped') {
    return (
      <div {...cardProps}>
        {groupedColumns(spec).map(column => {
          const slots = spec.slots.filter(slot => slot.group === column.group);
          const money = column.group === 'money';
          return (
            <div key={column.group} className={money ? 'lp-cell lp-cell--money' : 'lp-cell'}>
              {column.group === 'who' && title}
              {/* The top-right spot is the status's alone, even when this card has none. */}
              {money && spec.status > 0 && <div>{row.status && <StatusPill text={row.status}/>}</div>}
              {slots.map(slot => <Value key={slot.key} value={row.values[slot.key]}/>)}
            </div>
          );
        })}
        {clickable && <Chevron/>}
      </div>
    );
  }

  return (
    <div {...cardProps}>
      {rowColumns(spec).map(column => {
        if (column.kind === 'title') return <div key="title" className="lp-cell">{title}</div>;
        if (column.kind === 'status') {
          return (
            <div key="status" className="lp-cell lp-cell--status">
              {row.status && <StatusPill text={row.status}/>}
            </div>
          );
        }
        const slot = column.slot!;
        return (
          <div key={slot.key} className={slot.group === 'money' ? 'lp-cell lp-cell--money' : 'lp-cell'}>
            <Value value={row.values[slot.key]}/>
          </div>
        );
      })}
      {clickable && <Chevron/>}
    </div>
  );
};

/**
 * The cards, all in one layout at a time, picked from the list's own width
 * against the spec's switch points. A value too long for its slot wraps inside
 * that slot; only that card gets taller.
 */
export const ListCards = ({rows, spec}: {rows: ListRow[]; spec: CardSpec}) => {
  const [listRef, width] = useContentWidth<HTMLDivElement>();
  const link = useMapListLink();
  const beside = link?.split ? link : null;
  const points = useMemo(() => cardSwitchPoints(spec), [spec]);
  const layout: CardLayout = width >= points.row ? 'row' : width >= points.grouped ? 'grouped' : 'stacked';
  const template = layout === 'row'
    ? gridTemplate(rowColumns(spec))
    : layout === 'grouped' ? gridTemplate(groupedColumns(spec)) : undefined;
  return (
    <div
      ref={listRef}
      className={`lp-cards lp-cards--${layout}`}
      style={template ? {gridTemplateColumns: template} : undefined}
    >
      {rows.map(row => (
        <Card
          key={row.key}
          row={row}
          spec={spec}
          layout={layout}
          active={beside?.activeKey === row.key}
          onHover={beside ? over => beside.setHoveredKey(over ? row.key : null) : undefined}
        />
      ))}
    </div>
  );
};

// ---- Notes and the list body -------------------------------------------------

export const ListNote = ({text}: {text: string}) => <div className="lp-note">{text}</div>;

/** "Nothing here right now." / `Nothing here matches "query".` */
export const EmptyListNote = ({query}: {query: string}) => {
  const trimmed = query.trim();
  return <ListNote text={trimmed ? `Nothing here matches "${trimmed}".` : 'Nothing here right now.'}/>;
};

/** A spinner while loading, the error with Retry, the empty note, or the cards. */
export const ListBody = ({loading, error, onRetry, rows, spec, query, emptyNote}: {
  loading: boolean;
  error?: string | null;
  onRetry: () => void;
  rows: ListRow[];
  spec: CardSpec;
  query: string;
  emptyNote?: string;
}) => {
  if (loading) return <div className="lp-loading"><CircularProgress/></div>;
  if (error) {
    return (
      <div>
        <div className="lp-error">{error}</div>
        <button type="button" className="lp-retry" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (rows.length === 0) return emptyNote && !query.trim() ? <ListNote text={emptyNote}/> : <EmptyListNote query={query}/>;
  return <ListCards rows={rows} spec={spec}/>;
};

// ---- The page -----------------------------------------------------------------

/** The page area's scrolling column: starts where the side menu ends, runs to the right edge. */
export const ListPageScroll = ({children}: {children: ReactNode}) => (
  <div className="lp lp-frame">
    <div className="lp-scroll">{children}</div>
  </div>
);

/** A whole status list page: title row, filter/search box, and the list. */
export const StatusListPage = ({title, titleButton, searchLabel, list, spec, loading, error, onRetry, emptyNote}: {
  title: string;
  titleButton?: ReactNode;
  /** The filter/search box's label; undefined leaves the box off. */
  searchLabel?: string;
  list: StatusListState;
  spec: CardSpec;
  loading: boolean;
  error?: string | null;
  onRetry: () => void;
  emptyNote?: string;
}) => (
  <ListPageScroll>
    <ListPageTitleRow title={title} button={titleButton}/>
    {searchLabel && (
      <FilterSearchBox
        label={searchLabel}
        chips={list.chips}
        activeChip={list.activeChip}
        onChipChange={list.setActiveChip}
        query={list.query}
        onQueryChange={list.setQuery}
      />
    )}
    <ListBody
      loading={loading}
      error={error}
      onRetry={onRetry}
      rows={list.shown}
      spec={spec}
      query={list.query}
      emptyNote={emptyNote}
    />
  </ListPageScroll>
);

// ---- The list/map switch --------------------------------------------------------

const TabChevron = ({pointsLeft}: {pointsLeft: boolean}) => (
  <svg className="lp-tab-chevron" viewBox="0 0 24 24" aria-hidden="true"
       style={pointsLeft ? {transform: 'rotate(180deg)'} : undefined}>
    <path d="M8 3L17 12L8 21" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Android's ViewSwitchTab: a tab resting on an edge, its label on its side
 * against the flat edge and the chevron pointing the way you are about to go.
 * `start` rests on a left edge (flat side left), `end` on a right one.
 */
export const ViewSwitchTab = ({label, edge, onClick, ariaLabel, style}: {
  label: string;
  edge: 'start' | 'end';
  onClick: () => void;
  ariaLabel: string;
  style?: React.CSSProperties;
}) => (
  <button type="button" className={`lp-tab lp-tab--${edge}`} onClick={onClick} aria-label={ariaLabel} style={style}>
    <span className="lp-tab-body">
      {edge === 'end' && <TabChevron pointsLeft/>}
      <span className="lp-tab-label">{label}</span>
      {edge === 'start' && <TabChevron pointsLeft={false}/>}
    </span>
  </button>
);

/** From this page-area width the map opens beside the list; narrower, it replaces it. */
export const MAP_SIDE_BY_SIDE_MIN = 900;

/**
 * A list page with a map (Appointments, Jobs): the shared split. The map is
 * hidden until the MAP tab is pressed. Narrower than MAP_SIDE_BY_SIDE_MIN it
 * replaces the list, as on Android, and a LIST tab brings the list back; from
 * there up it opens on the left with the list panel on the right (a third of
 * the page area, never under 400px), and a tab on the line between them closes
 * it. The list panel lays itself out at its own width.
 *
 * The map and the list keep their place in the tree whichever way they are
 * laid out, so resizing across the switch point neither reloads the map nor
 * loses the list's scroll.
 */
export const MapListPage = ({list, map, mapOpen, onMapOpenChange, mapAvailable = true}: {
  list: ReactNode;
  /** A RecordsMap over the same list state. */
  map: ReactNode;
  mapOpen: boolean;
  onMapOpenChange: (open: boolean) => void;
  mapAvailable?: boolean;
}) => {
  const [frameRef, width] = useContentWidth<HTMLDivElement>();
  const showingMap = mapOpen && mapAvailable;
  const split = showingMap && width >= MAP_SIDE_BY_SIDE_MIN;
  const [activeKey, setActiveKey] = useState<RowKey | null>(null);
  const [hoveredKey, setHoveredKey] = useState<RowKey | null>(null);
  useEffect(() => {
    if (split) return;
    setActiveKey(null);
    setHoveredKey(null);
  }, [split]);
  const link = useMemo<MapListLink>(
    () => ({split, activeKey, setActiveKey, hoveredKey, setHoveredKey}),
    [split, activeKey, hoveredKey],
  );
  const closeTab = (style: React.CSSProperties) => (
    <ViewSwitchTab label="LIST" edge="end" onClick={() => onMapOpenChange(false)} ariaLabel="Close the map" style={style}/>
  );

  return (
    <MapListContext.Provider value={link}>
      <div ref={frameRef} className={split ? 'lp lp-frame lp-maplist lp-maplist--split' : 'lp lp-frame lp-maplist'}>
        {showingMap && <div className="lp-maplist-map">{map}</div>}
        {(!showingMap || split) && (
          <div className="lp-maplist-list">
            {list}
            {split && closeTab({right: '100%'})}
          </div>
        )}
        {!showingMap && mapAvailable && (
          <ViewSwitchTab label="MAP" edge="start" onClick={() => onMapOpenChange(true)} ariaLabel="Show the map" style={{left: 0}}/>
        )}
        {showingMap && !split && closeTab({right: 0})}
      </div>
    </MapListContext.Provider>
  );
};
